import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, Form, Nav, Tab, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  Archive,
  Bookmark,
  Briefcase,
  GitCompare,
  LayoutList,
  ListFilter,
  RefreshCw,
  Search,
  ShieldAlert,
  SlidersHorizontal,
} from 'lucide-react';
import { apiFetch } from '../utils/api';
import { getStoreId } from '../utils/storage';
import { toastSuccess, toastError, toastInfo } from '../utils/toast';
import Loading from './Loading';
import ConfirmModal from './ConfirmModal';
import StatusBadge, { accountTone, actionTone } from './StatusBadge';
import '../vault.css';
import {
  FILTER_TYPE_IDS,
  ITEMS_PAGE_SIZE,
  formatJobKind,
  formatRestoreAction,
  formatType,
  formatVaultItemName,
  sortVaultItems,
  summaryFromPayload,
} from '../utils/vaultUi';

const AUTOMATED_TYPES = [
  { id: 'product' },
  { id: 'category' },
  { id: 'product_type' },
  { id: 'discount_coupon' },
  { id: 'customer' },
  { id: 'customer_group' },
  { id: 'promotion' },
  { id: 'size_chart' },
  { id: 'order' },
];

const BY_REQUEST_TYPES = [
  { id: 'all' },
  { id: 'review' },
];

const NON_RESTORABLE = new Set(['review']);

const VAULT_TABS = [
  { key: 'all', labelKey: 'vault.tabs.items', icon: LayoutList },
  { key: 'advanced', labelKey: 'vault.tabs.advanced', icon: GitCompare },
  { key: 'points', labelKey: 'vault.tabs.points', icon: Bookmark },
  { key: 'jobs', labelKey: 'vault.tabs.jobs', icon: Briefcase },
];

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function isStale(lastBackupAt) {
  if (!lastBackupAt) return true;
  const then = new Date(lastBackupAt).getTime();
  if (Number.isNaN(then)) return true;
  return Date.now() - then > 15 * 60 * 1000;
}

function payloadOf(data) {
  if (!data || typeof data !== 'object') return null;
  if (data.payload && typeof data.payload === 'object') return data.payload;
  return data;
}

function itemAction(item, t) {
  if (item.is_deleted || item.deleted_at) return t('vault.itemAction.deleted');
  return item.last_action || item.action || '—';
}

function itemUpdated(item) {
  return item.updated_on || item.updated_at || item.last_seen_at;
}

export default function Vault({ refreshKey = 0, onUpgrade }) {
  const { t } = useTranslation();
  const hashSiteId = getStoreId();
  const [tab, setTab] = useState('all');
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [itemsTotal, setItemsTotal] = useState(0);
  const [itemsPage, setItemsPage] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);
  const [itemsRefreshing, setItemsRefreshing] = useState(false);
  const itemsLoadLock = useRef(false);
  const [search, setSearch] = useState('');
  const [typeChecks, setTypeChecks] = useState({});
  const [showing, setShowing] = useState('active');
  const [updatedFrom, setUpdatedFrom] = useState('');
  const [updatedTo, setUpdatedTo] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({
    types: [],
    showing: 'active',
    updatedFrom: '',
    updatedTo: '',
    q: '',
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pinLabel, setPinLabel] = useState('');
  const [backupMode, setBackupMode] = useState('items'); // 'items' | 'pin'
  const [backupStarting, setBackupStarting] = useState(false);

  const [versionsItem, setVersionsItem] = useState(null);
  const [versions, setVersions] = useState([]);
  const [livePayload, setLivePayload] = useState(null);
  const [selectedVersionId, setSelectedVersionId] = useState('');
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [viewMode, setViewMode] = useState('summary');
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const [automated, setAutomated] = useState({});
  const [byRequest, setByRequest] = useState({});
  const [asOf, setAsOf] = useState('');
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobDetail, setJobDetail] = useState(null);

  const [confirm, setConfirm] = useState(null);

  const pinCount = status?.pin_count ?? status?.snapshots?.length ?? 0;
  const backupRunning = Boolean(status?.backup_in_progress);
  const entitled = status?.entitled !== false && status?.account_type !== 'frozen';
  const accountType = status?.account_type || 'trial';
  const pinMax =
    status?.pin_max ?? (String(accountType).toLowerCase() === 'trial' ? 1 : 7);

  const backupLabel = useMemo(() => {
    if (!entitled) return t('vault.actions.upgradeToBackupNow');
    if (backupRunning || backupStarting) return t('vault.actions.backupInProgress');
    return t('vault.actions.backupNow');
  }, [entitled, backupRunning, backupStarting, t]);

  const loadJobs = useCallback(async (opts) => {
    if (!hashSiteId) return;
    if (!opts?.silent) setJobsLoading(true);
    try {
      const data = await apiFetch('/api/jobs', {}, { id: hashSiteId, limit: '50' });
      const list = Array.isArray(data.jobs) ? data.jobs : Array.isArray(data) ? data : [];
      setJobs(list);
    } finally {
      if (!opts?.silent) setJobsLoading(false);
    }
  }, [hashSiteId]);

  function goToJobsAfterRestore() {
    setTab('jobs');
    loadJobs().catch((err) => toastError(err, t('vault.toasts.couldNotLoadJobs'), 'jobs'));
  }

  async function openJobResults(job) {
    if (!hashSiteId) return;
    setJobDetail({
      jobId: job.id,
      title: t('vault.jobs.resultsTitleWithKind', {
        kind: formatJobKind(job.kind),
        id: job.id,
      }),
      loading: true,
      results: [],
    });
    try {
      const data = await apiFetch(`/api/jobs/${job.id}`, {}, { id: hashSiteId });
      setJobDetail({
        jobId: job.id,
        title: t('vault.jobs.resultsTitleWithKind', {
          kind: formatJobKind(data.job?.kind || job.kind),
          id: job.id,
        }),
        loading: false,
        results: Array.isArray(data.results) ? data.results : [],
        jobError: data.job?.error || null,
      });
    } catch (err) {
      setJobDetail({
        jobId: job.id,
        title: t('vault.jobs.resultsTitleWithKind', {
          kind: formatJobKind(job.kind),
          id: job.id,
        }),
        loading: false,
        results: [],
        error: err.message || t('vault.toasts.couldNotLoadJobResults'),
      });
    }
  }

  const loadStatus = useCallback(async () => {
    if (!hashSiteId) return null;
    const [settingsData, pointsData, statusData] = await Promise.all([
      apiFetch('/api/settings', {}, { id: hashSiteId }),
      apiFetch('/api/backup/restore-points', {}, { id: hashSiteId }).catch(() => ({
        snapshots: [],
      })),
      apiFetch('/api/backup/status', {}, { id: hashSiteId }).catch(() => null),
    ]);
    const snapshots = pointsData?.snapshots || pointsData?.items || statusData?.snapshots || [];
    const accountTypeGuess =
      statusData?.account_type || settingsData?.account_type || settingsData?.settings?.account_type || 'trial';
    const merged = {
      ...settingsData,
      ...(statusData || {}),
      snapshots,
      pin_count: pointsData?.pin_count ?? statusData?.pin_count ?? snapshots.length,
      pin_max:
        pointsData?.pin_max ??
        statusData?.pin_max ??
        (String(accountTypeGuess).toLowerCase() === 'trial' ? 1 : 7),
    };
    setStatus(merged);
    return merged;
  }, [hashSiteId]);

  const fetchItemsPage = useCallback(
    async (opts) => {
      if (!hashSiteId) return;
      if (itemsLoadLock.current) return;

      let page = Math.max(1, Number(opts.page) || 1);
      const isInitial = Boolean(opts.initial);
      itemsLoadLock.current = true;
      if (isInitial && !opts.silent) setLoading(true);
      else if (!opts.silent) setPageLoading(true);

      try {
        const selectedTypes = opts.filters.types;

        const requestPage = async (pageNum) => {
          const offset = (pageNum - 1) * ITEMS_PAGE_SIZE;
          const params = {
            id: hashSiteId,
            q: opts.filters.q,
            showing: opts.filters.showing,
            limit: String(ITEMS_PAGE_SIZE),
            offset: String(offset),
          };
          if (selectedTypes.length === 1) params.type = selectedTypes[0];
          if (selectedTypes.length > 0) params.types = selectedTypes.join(',');
          if (opts.filters.updatedFrom) {
            params.updated_from = new Date(opts.filters.updatedFrom).toISOString();
          }
          if (opts.filters.updatedTo) {
            params.updated_to = new Date(opts.filters.updatedTo).toISOString();
          }

          const data = await apiFetch('/api/backup/items', {}, params);
          const list = Array.isArray(data) ? data : data.items || data.data || [];
          const total = Number(data.total ?? list.length);
          return {
            sortedPage: sortVaultItems(list),
            total: Number.isFinite(total) ? total : list.length,
          };
        };

        let result = await requestPage(page);
        const totalPages = Math.max(1, Math.ceil(result.total / ITEMS_PAGE_SIZE) || 1);
        if (result.total > 0 && page > totalPages) {
          page = totalPages;
          result = await requestPage(page);
        }

        setItems(result.sortedPage);
        setItemsTotal(result.total);
        setItemsPage(page);
      } finally {
        itemsLoadLock.current = false;
        if (isInitial && !opts.silent) setLoading(false);
        setPageLoading(false);
      }
    },
    [hashSiteId],
  );

  const applyFiltersAndReload = useCallback(async () => {
    const next = {
      types: Object.entries(typeChecks)
        .filter(([, on]) => on)
        .map(([id]) => id),
      showing,
      updatedFrom,
      updatedTo,
      q: search.trim(),
    };
    setAppliedFilters(next);
    await fetchItemsPage({ page: 1, filters: next });
  }, [typeChecks, showing, updatedFrom, updatedTo, search, fetchItemsPage]);

  const goToItemsPage = useCallback(
    async (page) => {
      if (page < 1 || page === itemsPage || pageLoading) return;
      try {
        await fetchItemsPage({ page, filters: appliedFilters });
      } catch (err) {
        toastError(err, t('vault.toasts.couldNotLoadMoreItems'), 'items-page');
      }
    },
    [itemsPage, pageLoading, fetchItemsPage, appliedFilters, t],
  );

  const refreshItems = useCallback(async () => {
    if (!hashSiteId || itemsRefreshing) return;
    setItemsRefreshing(true);
    try {
      await Promise.all([
        fetchItemsPage({ page: itemsPage, filters: appliedFilters, silent: true }),
        loadStatus(),
      ]);
    } catch (err) {
      toastError(err, t('vault.toasts.couldNotRefreshItems'), 'refresh-items');
    } finally {
      setItemsRefreshing(false);
    }
  }, [hashSiteId, itemsRefreshing, fetchItemsPage, itemsPage, appliedFilters, loadStatus, t]);

  async function handleExport() {
    if (!hashSiteId || backupRunning || exporting) return;
    setExporting(true);
    try {
      const params = {
        id: hashSiteId,
        q: appliedFilters.q,
        showing: appliedFilters.showing,
        limit: '5000',
      };
      if (appliedFilters.types.length === 1) params.type = appliedFilters.types[0];
      if (appliedFilters.types.length > 0) params.types = appliedFilters.types.join(',');
      if (appliedFilters.updatedFrom) {
        params.updated_from = new Date(appliedFilters.updatedFrom).toISOString();
      }
      if (appliedFilters.updatedTo) {
        params.updated_to = new Date(appliedFilters.updatedTo).toISOString();
      }
      const data = await apiFetch('/api/export', {}, params);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backuply-vault-${status?.storeHash || status?.store_id || 'export'}-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toastSuccess(t('vault.toasts.exportDownloaded'));
    } catch (err) {
      toastError(err, t('vault.toasts.couldNotExport'), 'export');
    } finally {
      setExporting(false);
    }
  }

  useEffect(() => {
    if (!hashSiteId) return undefined;
    let cancelled = false;
    async function boot() {
      setLoading(true);
      try {
        await loadStatus();
        if (!cancelled) {
          await fetchItemsPage({ page: 1, filters: appliedFilters, initial: true });
        }
      } catch (err) {
        if (!cancelled) toastError(err, t('vault.toasts.couldNotLoadVault'), 'vault-boot');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    boot();
    return () => {
      cancelled = true;
    };
  }, [hashSiteId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!hashSiteId || tab !== 'jobs') return undefined;
    loadJobs().catch((err) => toastError(err, t('vault.toasts.couldNotLoadJobs'), 'jobs-tab'));
    const timer = setInterval(() => {
      loadJobs({ silent: true }).catch(() => {});
    }, 8000);
    return () => clearInterval(timer);
  }, [hashSiteId, tab, loadJobs]);

  useEffect(() => {
    if (!backupRunning) return undefined;
    const timer = setInterval(() => {
      loadStatus().catch(() => {});
    }, 4000);
    return () => clearInterval(timer);
  }, [backupRunning, loadStatus]);

  function askBackupNow() {
    if (!hashSiteId || backupRunning || backupStarting) return;
    if (!entitled) {
      onUpgrade?.();
      return;
    }
    setBackupMode('items');
    setPinLabel('');
    setConfirm({
      kind: 'backup-now',
      title: t('vault.confirms.backupNow.title'),
      confirmLabel: t('vault.confirms.backupNow.confirmLabel'),
      variant: 'primary',
    });
  }

  async function startBackupNow({ pin, label }) {
    if (!hashSiteId) return;
    const usePin = Boolean(pin) && pinCount < pinMax;
    await apiFetch(
      '/api/backup/now',
      {
        method: 'POST',
        body: JSON.stringify({
          pin: usePin,
          label: usePin ? label || undefined : undefined,
        }),
      },
      { id: hashSiteId },
    );
    toastSuccess(
      usePin ? t('vault.toasts.backupStartedPinned') : t('vault.toasts.backupStarted'),
    );
    await loadStatus();
  }

  async function handleBackupNow() {
    askBackupNow();
  }

  async function openVersions(item) {
    setVersionsItem(item);
    setSelectedVersionId('');
    setSelectedVersion(null);
    setViewMode('summary');
    setVersionsLoading(true);
    try {
      const data = await apiFetch(
        `/api/backup/items/${item.id}/versions`,
        {},
        { id: hashSiteId },
      );
      setLivePayload(payloadOf(data.live) || payloadOf(data.current) || null);
      setVersions(data.versions || data.data || []);
    } catch (err) {
      toastError(err, t('vault.toasts.couldNotLoadVersionHistory'), 'versions');
      setVersionsItem(null);
    } finally {
      setVersionsLoading(false);
    }
  }

  async function selectVersion(versionId) {
    setSelectedVersionId(versionId);
    if (!versionId) {
      setSelectedVersion(null);
      return;
    }
    try {
      const data = await apiFetch(`/api/backup/versions/${versionId}`, {}, { id: hashSiteId });
      setSelectedVersion(data);
    } catch (err) {
      toastError(err, t('vault.toasts.couldNotLoadVersion'), 'version-detail');
    }
  }

  function askRestoreItem() {
    if (!versionsItem || !selectedVersionId) return;
    if (NON_RESTORABLE.has(String(versionsItem.resource_type).toLowerCase())) {
      toastInfo(t('vault.toasts.reviewsNotRestorable'));
      return;
    }
    if (backupRunning) {
      toastInfo(t('vault.toasts.waitForBackupBeforeRestore'));
      return;
    }
    setConfirm({
      kind: 'restore-item',
      title: t('vault.confirms.restoreItem.title'),
      body: (
        <>
          <p className="mb-2">
            {t('vault.confirms.restoreItem.body', {
              name: formatVaultItemName(versionsItem) || t('vault.versions.thisItem'),
            })}
          </p>
          <p className="mb-0 text-body-secondary small">{t('vault.confirms.restoreItem.tip')}</p>
        </>
      ),
      confirmLabel: t('vault.confirms.restoreItem.confirmLabel'),
      variant: 'primary',
    });
  }

  function askRestoreAdvanced() {
    const automatedTypes = Object.entries(automated)
      .filter(([, on]) => on)
      .map(([id]) => id);
    const byRequestTypes = Object.entries(byRequest)
      .filter(([, on]) => on)
      .map(([id]) => id);
    if (!asOf || (automatedTypes.length === 0 && byRequestTypes.length === 0)) {
      toastInfo(t('vault.toasts.chooseDateAndType'));
      return;
    }
    if (backupRunning) {
      toastInfo(t('vault.toasts.waitForBackupBeforeRestore'));
      return;
    }
    setConfirm({
      kind: 'restore-advanced',
      title: t('vault.confirms.restoreAdvanced.title'),
      body: (
        <>
          <p className="mb-2">
            {t('vault.confirms.restoreAdvanced.body', { date: formatDate(asOf) })}
          </p>
          <p className="mb-0 text-body-secondary small">
            {t('vault.confirms.restoreAdvanced.note')}
          </p>
        </>
      ),
      confirmLabel: byRequestTypes.length
        ? t('vault.confirms.restoreAdvanced.confirmLabelByRequest')
        : t('vault.confirms.restoreAdvanced.confirmLabel'),
      variant: 'primary',
      automatedTypes,
      byRequestTypes,
    });
  }

  function askRestoreSnapshot(snapshot) {
    const name = snapshot.label || t('vault.points.fallbackLabel', { id: snapshot.id });
    setConfirm({
      kind: 'restore-snapshot',
      title: t('vault.confirms.restoreSnapshot.title'),
      body: (
        <p className="mb-0">
          {t('vault.confirms.restoreSnapshot.body', {
            name,
            date: formatDate(snapshot.created_at),
          })}
        </p>
      ),
      confirmLabel: t('vault.confirms.restoreSnapshot.confirmLabel'),
      variant: 'primary',
      snapshot,
    });
  }

  function askDeleteSnapshot(snapshot) {
    const name = snapshot.label || t('vault.points.fallbackLabel', { id: snapshot.id });
    setConfirm({
      kind: 'delete-snapshot',
      title: t('vault.confirms.deleteSnapshot.title'),
      body: (
        <p className="mb-0">{t('vault.confirms.deleteSnapshot.body', { name })}</p>
      ),
      confirmLabel: t('vault.confirms.deleteSnapshot.confirmLabel'),
      variant: 'danger',
      snapshot,
    });
  }

  async function runConfirmed() {
    if (!confirm) return;
    const busy = confirm.kind === 'backup-now' ? setBackupStarting : setRestoring;
    busy(true);
    try {
      if (confirm.kind === 'backup-now') {
        await startBackupNow({
          pin: backupMode === 'pin',
          label: pinLabel.trim(),
        });
      } else if (confirm.kind === 'restore-item') {
        await apiFetch(
          '/api/backup/restore/item',
          {
            method: 'POST',
            body: JSON.stringify({
              item_id: versionsItem.id,
              version_id: selectedVersionId,
            }),
          },
          { id: hashSiteId },
        );
        toastSuccess(t('vault.toasts.restoreStarted'));
        setVersionsItem(null);
        goToJobsAfterRestore();
      } else if (confirm.kind === 'restore-advanced') {
        if (confirm.automatedTypes?.length) {
          await apiFetch(
            '/api/backup/restore/advanced',
            {
              method: 'POST',
              body: JSON.stringify({
                as_of: asOf,
                mode: 'automated',
                resource_types: confirm.automatedTypes,
              }),
            },
            { id: hashSiteId },
          );
        }
        if (confirm.byRequestTypes?.length) {
          await apiFetch(
            '/api/backup/restore/advanced',
            {
              method: 'POST',
              body: JSON.stringify({
                as_of: asOf,
                mode: 'by_request',
                resource_types: confirm.byRequestTypes,
              }),
            },
            { id: hashSiteId },
          );
        }
        toastSuccess(
          confirm.byRequestTypes?.length
            ? t('vault.toasts.requestReceived')
            : t('vault.toasts.advancedRestoreStarted'),
        );
        if (!confirm.byRequestTypes?.length) goToJobsAfterRestore();
      } else if (confirm.kind === 'restore-snapshot') {
        await apiFetch(
          '/api/backup/restore/advanced',
          {
            method: 'POST',
            body: JSON.stringify({ kind: 'named_pin', snapshot_id: confirm.snapshot.id }),
          },
          { id: hashSiteId },
        );
        toastSuccess(t('vault.toasts.restoreFromPointStarted'));
        goToJobsAfterRestore();
      } else if (confirm.kind === 'delete-snapshot') {
        await apiFetch(
          `/api/backup/restore-points/${confirm.snapshot.id}`,
          { method: 'DELETE' },
          { id: hashSiteId },
        );
        toastSuccess(t('vault.toasts.restorePointDeleted'));
        await loadStatus();
      }
      setConfirm(null);
    } catch (err) {
      if (confirm.kind === 'backup-now') {
        toastError(err, t('vault.toasts.couldNotStartBackup'), 'backup-now');
      } else {
        toastError(err, t('vault.toasts.couldNotCompleteAction'), confirm.kind);
      }
    } finally {
      busy(false);
    }
  }

  const liveSummary = summaryFromPayload(versionsItem?.resource_type || '', livePayload);
  const selectedPayload = payloadOf(selectedVersion);
  const selectedSummary = summaryFromPayload(versionsItem?.resource_type || '', selectedPayload);
  const restoreDisabled =
    backupRunning ||
    restoring ||
    !selectedVersionId ||
    NON_RESTORABLE.has(String(versionsItem?.resource_type || '').toLowerCase());

  const advancedReady =
    Boolean(asOf) &&
    (Object.values(automated).some(Boolean) || Object.values(byRequest).some(Boolean)) &&
    !backupRunning &&
    !restoring;

  const progressPct = Math.min(
    100,
    Math.max(5, Number(status?.backup_progress_percent || status?.progress || 8)),
  );

  const activeFilterCount = useMemo(
    () =>
      appliedFilters.types.length +
      (appliedFilters.updatedFrom ? 1 : 0) +
      (appliedFilters.updatedTo ? 1 : 0) +
      (appliedFilters.showing !== 'active' ? 1 : 0) +
      (appliedFilters.q ? 1 : 0),
    [appliedFilters],
  );

  const itemsTotalPages = Math.max(1, Math.ceil(itemsTotal / ITEMS_PAGE_SIZE) || 1);
  const itemsRangeFrom = itemsTotal === 0 ? 0 : (itemsPage - 1) * ITEMS_PAGE_SIZE + 1;
  const itemsRangeTo = itemsTotal === 0 ? 0 : Math.min(itemsPage * ITEMS_PAGE_SIZE, itemsTotal);

  function clearFilters() {
    const next = {
      types: [],
      showing: 'active',
      updatedFrom: '',
      updatedTo: '',
      q: '',
    };
    setSearch('');
    setTypeChecks({});
    setShowing('active');
    setUpdatedFrom('');
    setUpdatedTo('');
    setAppliedFilters(next);
    fetchItemsPage({ page: 1, filters: next }).catch((err) =>
      toastError(err, t('vault.toasts.couldNotClearFilters'), 'clear-filters'),
    );
  }

  function toggleTypeFilter(typeId) {
    setTypeChecks((prev) => ({ ...prev, [typeId]: !prev[typeId] }));
  }

  function jobStatusTone(statusValue) {
    const s = String(statusValue || '').toLowerCase();
    if (s === 'complete') return 'success';
    if (s === 'failed') return 'danger';
    if (s === 'running' || s === 'queued') return 'info';
    return 'muted';
  }

  return (
    <div className="vault-shell">
      <section className="vault-dashboard vault-dashboard-compact">
        <div className="vault-dashboard-top">
          <div className="vault-dashboard-brand">
            <h1 className="vault-title">{t('vault.title')}</h1>
            <span className="vault-store-id">{status?.storeHash || status?.store_id || '…'}</span>
            <StatusBadge tone={accountTone(accountType)}>
              {String(accountType).replace(/_/g, ' ')}
            </StatusBadge>
          </div>
          <div className="vault-dashboard-actions">
            <Button
              className="btn-vault-primary vault-cta"
              size="sm"
              disabled={backupRunning || backupStarting}
              onClick={handleBackupNow}
            >
              {(backupRunning || backupStarting) && (
                <Spinner size="sm" className="me-2" animation="border" />
              )}
              {backupLabel}
            </Button>
            {!entitled ? (
              <Button variant="outline-secondary" size="sm" onClick={() => onUpgrade?.()}>
                {t('vault.actions.viewPlans')}
              </Button>
            ) : (
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={backupRunning || exporting}
                onClick={handleExport}
              >
                {exporting ? t('vault.actions.exporting') : t('vault.actions.export')}
              </Button>
            )}
          </div>
        </div>

        <div className="vault-stat-row">
          <div className="vault-stat-item">
            <span className="vault-stat-item-label">{t('vault.stats.lastBackup')}</span>
            <span className="vault-stat-item-value">{formatDate(status?.last_backup_at)}</span>
          </div>
          <div className="vault-stat-item">
            <span className="vault-stat-item-label">{t('vault.stats.next')}</span>
            <span className="vault-stat-item-value">{formatDate(status?.next_backup_at)}</span>
          </div>
          <div className="vault-stat-item">
            <span className="vault-stat-item-label">{t('vault.stats.restorePoints')}</span>
            <span className="vault-stat-item-value">
              {pinCount}/{pinMax}
            </span>
          </div>
          <div className="vault-stat-item">
            <span className="vault-stat-item-label">{t('vault.stats.items')}</span>
            <span className="vault-stat-item-value">
              {itemsTotal > 0 ? itemsTotal.toLocaleString() : '0'}
            </span>
          </div>
        </div>
      </section>

      <div className="vault-alert-stack">
        {backupRunning ? (
          <div className="vault-banner info">
            <div className="vault-banner-icon">
              <RefreshCw size={16} className="spin" />
            </div>
            <div className="vault-banner-body">
              <div className="vault-banner-title">{t('vault.banner.backupInProgress.title')}</div>
              <div className="vault-progress" aria-hidden="true">
                <span style={{ width: `${progressPct}%` }} />
              </div>
              <p>
                {status?.backup_progress_message ||
                  t('vault.banner.backupInProgress.body')}
              </p>
            </div>
          </div>
        ) : null}

        {isStale(status?.last_backup_at) && !backupRunning ? (
          <div className="vault-banner warn">
            <div className="vault-banner-icon">
              <ShieldAlert size={16} />
            </div>
            <div className="vault-banner-body">
              <div className="vault-banner-title">
                {t('vault.banner.recommendedBeforeRestore.title')}
              </div>
              <p>{t('vault.banner.recommendedBeforeRestore.body')}</p>
            </div>
          </div>
        ) : null}
      </div>

      <section className="vault-panel">
        <div className="vault-panel-head">
          <div className="vault-segmented" role="tablist" aria-label={t('vault.aria.sections')}>
            {VAULT_TABS.map(({ key, labelKey, icon: Icon }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                className={`vault-segment ${tab === key ? 'active' : ''}`}
                onClick={() => setTab(key)}
              >
                <Icon size={15} />
                {t(labelKey)}
              </button>
            ))}
          </div>
        </div>

        <div className="vault-panel-body">
      <Tab.Container activeKey={tab} onSelect={(k) => setTab(k || 'all')}>
        <Tab.Content>
          <Tab.Pane eventKey="all">
            <div className="vault-filter-zone">
              <div className="vault-filter-zone-head">
                <span className="vault-filter-zone-title">
                  <ListFilter size={14} /> {t('vault.filters.title')}
                </span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="vault-refresh-btn"
                  disabled={loading || itemsRefreshing}
                  onClick={() =>
                    refreshItems().catch((err) =>
                      toastError(err, t('vault.toasts.couldNotRefreshItems'), 'refresh-items'),
                    )
                  }
                >
                  <RefreshCw size={14} className={itemsRefreshing ? 'spin' : undefined} />
                  {itemsRefreshing ? t('vault.actions.refreshing') : t('vault.actions.refresh')}
                </Button>
              </div>

              <div className="vault-toolbar">
                <div className="vault-search">
                  <Search size={16} />
                  <Form.Control
                    size="sm"
                    placeholder={t('vault.filters.searchPlaceholder')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        applyFiltersAndReload().catch((err) =>
                          toastError(err, t('vault.toasts.searchFailed'), 'search'),
                        );
                      }
                    }}
                  />
                </div>
                <Form.Select size="sm" value={showing} onChange={(e) => setShowing(e.target.value)}>
                  <option value="active">{t('vault.filters.showing.active')}</option>
                  <option value="deleted">{t('vault.filters.showing.deleted')}</option>
                  <option value="all">{t('vault.filters.showing.all')}</option>
                </Form.Select>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="vault-toolbar-btn"
                  onClick={() => setFiltersOpen((v) => !v)}
                >
                  <SlidersHorizontal size={15} />
                  {filtersOpen ? t('vault.actions.hideFilters') : t('vault.actions.moreFilters')}
                  {activeFilterCount > 0 ? (
                    <span className="vault-filter-badge">{activeFilterCount}</span>
                  ) : null}
                </Button>
                <Button
                  size="sm"
                  className="btn-vault-primary vault-toolbar-btn"
                  onClick={() =>
                    applyFiltersAndReload().catch((err) =>
                      toastError(err, t('vault.toasts.couldNotApplyFilters'), 'filter'),
                    )
                  }
                >
                  {t('vault.actions.apply')}
                </Button>
              </div>

              {filtersOpen ? (
                <div className="vault-filter-drawer">
                  <div className="vault-filter-drawer-head">
                    <h3>{t('vault.filters.refineResults')}</h3>
                    {activeFilterCount > 0 ? (
                      <button type="button" className="vault-filter-clear" onClick={clearFilters}>
                        {t('vault.actions.clearAll')}
                      </button>
                    ) : null}
                  </div>
                  <div className="vault-filter-drawer-body">
                    <div className="vault-filter-drawer-section">
                      <p className="vault-filter-section-label">{t('vault.filters.itemTypes')}</p>
                      <div className="vault-type-grid">
                        {FILTER_TYPE_IDS.map((typeId) => (
                          <button
                            key={typeId}
                            type="button"
                            className={`vault-type-toggle ${typeChecks[typeId] ? 'on' : ''}`}
                            onClick={() => toggleTypeFilter(typeId)}
                          >
                            {formatType(typeId)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="vault-filter-drawer-section">
                      <p className="vault-filter-section-label">{t('vault.filters.updatedBetween')}</p>
                      <div className="vault-date-grid">
                        <Form.Group>
                          <Form.Label className="vault-date-label">{t('vault.filters.from')}</Form.Label>
                          <Form.Control
                            size="sm"
                            type="datetime-local"
                            value={updatedFrom}
                            onChange={(e) => setUpdatedFrom(e.target.value)}
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label className="vault-date-label">{t('vault.filters.to')}</Form.Label>
                          <Form.Control
                            size="sm"
                            type="datetime-local"
                            value={updatedTo}
                            onChange={(e) => setUpdatedTo(e.target.value)}
                          />
                        </Form.Group>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="vault-items-meta">
                <div className="vault-items-meta-left">
                  <span>
                    {t('vault.filters.metaRange', {
                      from: itemsRangeFrom.toLocaleString(),
                      to: itemsRangeTo.toLocaleString(),
                      total: (itemsTotal || 0).toLocaleString(),
                    })}
                  </span>
                  {pageLoading ? <span>{t('vault.filters.loadingPage')}</span> : null}
                </div>
              </div>
            </div>

            <div className="vault-table-wrap">
              {loading ? (
                <Loading message={t('vault.loading.vaultItems')} />
              ) : items.length === 0 ? (
                <div className="vault-empty">
                  <div className="vault-empty-icon">
                    <Archive size={24} />
                  </div>
                  <h3>{t('vault.empty.items.title')}</h3>
                  <p>{t('vault.empty.items.body')}</p>
                  <Button
                    className="btn-vault-primary mt-3"
                    disabled={backupRunning || backupStarting}
                    onClick={handleBackupNow}
                  >
                    {backupLabel}
                  </Button>
                </div>
              ) : (
                <>
                  <table className="vault-table vault-table-desktop">
                    <thead>
                      <tr>
                        <th>{t('vault.table.itemType')}</th>
                        <th>{t('vault.table.name')}</th>
                        <th>{t('vault.table.action')}</th>
                        <th>{t('vault.table.updatedOn')}</th>
                        <th aria-label={t('vault.aria.actions')} />
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => {
                        const action = itemAction(item, t);
                        return (
                          <tr key={String(item.id)}>
                            <td>
                              <span className="type-chip">{formatType(item.resource_type)}</span>
                            </td>
                            <td className="vault-name-cell">{formatVaultItemName(item)}</td>
                            <td>
                              <StatusBadge tone={actionTone(action)}>{action}</StatusBadge>
                            </td>
                            <td className="text-nowrap">{formatDate(itemUpdated(item))}</td>
                            <td className="text-end">
                              <Button
                                size="sm"
                                variant="link"
                                className="vault-row-action"
                                onClick={() => openVersions(item)}
                              >
                                {t('vault.actions.viewVersions')}
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="vault-card-list" role="list">
                    {items.map((item) => {
                      const action = itemAction(item, t);
                      return (
                        <article key={`card-${item.id}`} className="vault-item-card" role="listitem">
                          <div className="vault-item-card-top">
                            <span className="type-chip">{formatType(item.resource_type)}</span>
                            <StatusBadge tone={actionTone(action)}>{action}</StatusBadge>
                          </div>
                          <div className="vault-name-cell">{formatVaultItemName(item)}</div>
                          <div className="vault-item-card-meta">{formatDate(itemUpdated(item))}</div>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="vault-item-card-action"
                            onClick={() => openVersions(item)}
                          >
                            {t('vault.actions.viewVersions')}
                          </Button>
                        </article>
                      );
                    })}
                  </div>
                  {itemsTotal > ITEMS_PAGE_SIZE ? (
                    <nav className="vault-pagination" aria-label={t('vault.aria.pagination')}>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={itemsPage <= 1 || pageLoading}
                        onClick={() => goToItemsPage(itemsPage - 1)}
                      >
                        {t('vault.pagination.previous')}
                      </Button>
                      <span className="vault-pagination-status">
                        {t('vault.pagination.pageOf', {
                          page: itemsPage,
                          pages: itemsTotalPages,
                        })}
                      </span>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={itemsPage >= itemsTotalPages || pageLoading}
                        onClick={() => goToItemsPage(itemsPage + 1)}
                      >
                        {t('vault.pagination.next')}
                      </Button>
                    </nav>
                  ) : null}
                </>
              )}
            </div>
          </Tab.Pane>

          <Tab.Pane eventKey="advanced">
            <div className="advanced-grid">
              <div className="advanced-card will">
                <h3>{t('vault.advanced.will.title')}</h3>
                <ul>
                  <li>{t('vault.advanced.will.restoreDeleted')}</li>
                  <li>{t('vault.advanced.will.updateModified')}</li>
                </ul>
              </div>
              <div className="advanced-card wont">
                <h3>{t('vault.advanced.wont.title')}</h3>
                <ul>
                  <li>{t('vault.advanced.wont.deleteLive')}</li>
                  <li>{t('vault.advanced.wont.overwriteUnchanged')}</li>
                </ul>
              </div>
            </div>

            <Form.Group className="mb-4" style={{ maxWidth: 340 }}>
              <Form.Label className="fw-semibold">{t('vault.advanced.dateAndTime')}</Form.Label>
              <Form.Control
                type="datetime-local"
                value={asOf}
                onChange={(e) => setAsOf(e.target.value)}
              />
            </Form.Group>

            <div className="row g-4 mb-4">
              <div className="col-md-6">
                <div className="restore-group">
                  <h3>{t('vault.advanced.automated.title')}</h3>
                  <p>{t('vault.advanced.automated.description')}</p>
                  {AUTOMATED_TYPES.map((typeDef) => (
                    <Form.Check
                      key={typeDef.id}
                      type="checkbox"
                      id={`auto-${typeDef.id}`}
                      label={t(`vault.advanced.automated.types.${typeDef.id}`)}
                      checked={Boolean(automated[typeDef.id])}
                      onChange={(e) =>
                        setAutomated((prev) => ({ ...prev, [typeDef.id]: e.target.checked }))
                      }
                    />
                  ))}
                </div>
              </div>
              <div className="col-md-6">
                <div className="restore-group muted">
                  <h3>{t('vault.advanced.byRequest.title')}</h3>
                  <p>{t('vault.advanced.byRequest.description')}</p>
                  {BY_REQUEST_TYPES.map((typeDef) => (
                    <Form.Check
                      key={typeDef.id}
                      type="checkbox"
                      id={`req-${typeDef.id}`}
                      label={t(`vault.advanced.byRequest.types.${typeDef.id}`)}
                      checked={Boolean(byRequest[typeDef.id])}
                      onChange={(e) =>
                        setByRequest((prev) => ({ ...prev, [typeDef.id]: e.target.checked }))
                      }
                    />
                  ))}
                </div>
              </div>
            </div>

            <Button
              className="btn-vault-primary"
              disabled={!advancedReady}
              onClick={askRestoreAdvanced}
            >
              {t('vault.actions.restoreItems')}
            </Button>
          </Tab.Pane>

          <Tab.Pane eventKey="points">
            {(status?.snapshots || []).length === 0 ? (
              <div className="vault-empty">
                <div className="vault-empty-icon">
                  <Bookmark size={24} />
                </div>
                <h3>{t('vault.empty.points.title')}</h3>
                <p>{t('vault.empty.points.body', { max: pinMax })}</p>
              </div>
            ) : (
              <div className="vault-table-wrap">
                <table className="vault-table vault-table-desktop">
                  <thead>
                    <tr>
                      <th>{t('vault.table.label')}</th>
                      <th>{t('vault.table.created')}</th>
                      <th>{t('vault.table.status')}</th>
                      <th aria-label={t('vault.aria.actions')} />
                    </tr>
                  </thead>
                  <tbody>
                    {(status.snapshots || []).map((snap) => (
                      <tr key={String(snap.id)}>
                        <td className="vault-name-cell">
                          {snap.label || t('vault.points.fallbackLabel', { id: snap.id })}
                        </td>
                        <td className="text-nowrap">{formatDate(snap.created_at)}</td>
                        <td>
                          <StatusBadge
                            tone={
                              snap.status === 'complete'
                                ? 'success'
                                : snap.status === 'failed'
                                  ? 'danger'
                                  : 'info'
                            }
                          >
                            {snap.status || '—'}
                          </StatusBadge>
                        </td>
                        <td className="text-end text-nowrap">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="me-2"
                            disabled={backupRunning || restoring || snap.status !== 'complete'}
                            onClick={() => askRestoreSnapshot(snap)}
                          >
                            {t('vault.actions.restore')}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            disabled={restoring}
                            onClick={() => askDeleteSnapshot(snap)}
                          >
                            {t('vault.actions.delete')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="vault-card-list" role="list">
                  {(status.snapshots || []).map((snap) => (
                    <article key={`snap-card-${snap.id}`} className="vault-item-card" role="listitem">
                      <div className="vault-item-card-top">
                        <div className="vault-name-cell">
                          {snap.label || t('vault.points.fallbackLabel', { id: snap.id })}
                        </div>
                        <StatusBadge
                          tone={
                            snap.status === 'complete'
                              ? 'success'
                              : snap.status === 'failed'
                                ? 'danger'
                                : 'info'
                          }
                        >
                          {snap.status || '—'}
                        </StatusBadge>
                      </div>
                      <div className="vault-item-card-meta">{formatDate(snap.created_at)}</div>
                      <div className="vault-item-card-actions">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          disabled={backupRunning || restoring || snap.status !== 'complete'}
                          onClick={() => askRestoreSnapshot(snap)}
                        >
                          {t('vault.actions.restore')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          disabled={restoring}
                          onClick={() => askDeleteSnapshot(snap)}
                        >
                          {t('vault.actions.delete')}
                        </Button>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            )}
          </Tab.Pane>

          <Tab.Pane eventKey="jobs">
            {jobsLoading && jobs.length === 0 ? (
              <Loading message={t('vault.loading.jobs')} />
            ) : jobs.length === 0 ? (
              <div className="vault-empty">
                <div className="vault-empty-icon">
                  <Briefcase size={24} />
                </div>
                <h3>{t('vault.empty.jobs.title')}</h3>
                <p>{t('vault.empty.jobs.body')}</p>
              </div>
            ) : (
              <div className="vault-table-wrap">
                <table className="vault-table vault-table-desktop">
                  <thead>
                    <tr>
                      <th>{t('vault.table.job')}</th>
                      <th>{t('vault.table.status')}</th>
                      <th>{t('vault.table.progress')}</th>
                      <th>{t('vault.table.started')}</th>
                      <th aria-label={t('vault.aria.actions')} />
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job) => {
                      const progress = job.progress != null ? Number(job.progress) : null;
                      return (
                        <tr key={String(job.id)}>
                          <td>
                            <div className="job-kind">{formatJobKind(job.kind)}</div>
                            <div className="text-body-secondary small">#{job.id}</div>
                          </td>
                          <td>
                            <StatusBadge tone={jobStatusTone(job.status)}>
                              {job.status || '—'}
                            </StatusBadge>
                          </td>
                          <td className="job-progress-cell">
                            {progress != null && Number.isFinite(progress) ? (
                              <>
                                {progress}%
                                <div className="job-progress-bar" aria-hidden="true">
                                  <span style={{ width: `${Math.min(100, Math.max(0, progress))}%` }} />
                                </div>
                              </>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="text-nowrap">{formatDate(job.started_at)}</td>
                          <td className="text-end">
                            <Button
                              size="sm"
                              variant="link"
                              className="vault-row-action"
                              onClick={() => openJobResults(job)}
                            >
                              {t('vault.actions.viewResults')}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
        </div>
      </section>

      <Modal
        show={Boolean(jobDetail)}
        onHide={() => setJobDetail(null)}
        size="lg"
        centered
        className="versions-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{jobDetail?.title || t('vault.jobs.resultsTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {jobDetail?.loading ? (
            <Loading message={t('vault.loading.jobResults')} />
          ) : jobDetail?.error ? (
            <p className="text-danger mb-0">{jobDetail.error}</p>
          ) : (
            <>
              {jobDetail?.jobError ? (
                <p className="text-danger small">{jobDetail.jobError}</p>
              ) : null}
              {jobDetail?.results?.length ? (
                <table className="vault-table">
                  <thead>
                    <tr>
                      <th>{t('vault.table.type')}</th>
                      <th>{t('vault.table.nameOrId')}</th>
                      <th>{t('vault.table.action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobDetail.results.map((row, idx) => (
                      <tr key={`${row.bc_id || idx}-${row.resource_type || ''}`}>
                        <td>{formatType(row.resource_type)}</td>
                        <td>{formatVaultItemName(row)}</td>
                        <td>{formatRestoreAction(row.action)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-body-secondary mb-0 small">{t('vault.jobs.noPerItemResults')}</p>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setJobDetail(null)}>
            {t('vault.actions.close')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(versionsItem)}
        onHide={() => setVersionsItem(null)}
        size="lg"
        centered
        fullscreen="md-down"
        className="versions-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <span className="versions-modal-kicker">{formatType(versionsItem?.resource_type)}</span>
            {formatVaultItemName(versionsItem) || t('vault.versions.itemFallback')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {versionsLoading ? (
            <Loading message={t('vault.loading.versions')} />
          ) : (
            <>
              <Form.Label className="fw-semibold">{t('vault.versions.version')}</Form.Label>
              <Form.Select
                className="mb-3"
                value={selectedVersionId}
                onChange={(e) => selectVersion(e.target.value)}
              >
                <option value="">{t('vault.versions.selectPlaceholder')}</option>
                {versions.map((v) => (
                  <option key={String(v.id)} value={String(v.id)}>
                    {v.source
                      ? t('vault.versions.optionWithSource', {
                          date: formatDate(v.captured_at),
                          source: v.source,
                        })
                      : formatDate(v.captured_at)}
                  </option>
                ))}
              </Form.Select>

              <Nav variant="pills" className="versions-pills mb-3" activeKey={viewMode} onSelect={setViewMode}>
                <Nav.Item>
                  <Nav.Link eventKey="summary">{t('vault.versions.summary')}</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="source">{t('vault.versions.source')}</Nav.Link>
                </Nav.Item>
              </Nav>

              {viewMode === 'summary' ? (
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="compare-pane">
                      <div className="compare-pane-head">
                        {t('vault.versions.currentVersion')}{' '}
                        <StatusBadge tone="success">{t('vault.versions.live')}</StatusBadge>
                      </div>
                      <dl className="vault-dl">
                        {Object.entries(liveSummary).map(([k, v]) => (
                          <React.Fragment key={k}>
                            <dt>{k}</dt>
                            <dd>{v}</dd>
                          </React.Fragment>
                        ))}
                      </dl>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="compare-pane">
                      <div className="compare-pane-head">{t('vault.versions.selectedVersion')}</div>
                      {selectedVersionId ? (
                        <dl className="vault-dl">
                          {Object.entries(selectedSummary).map(([k, v]) => (
                            <React.Fragment key={k}>
                              <dt>{k}</dt>
                              <dd>{v}</dd>
                            </React.Fragment>
                          ))}
                        </dl>
                      ) : (
                        <p className="text-body-secondary mb-0 small">
                          {t('vault.versions.selectToCompare')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <pre className="vault-source">
                  {JSON.stringify(selectedPayload || livePayload || {}, null, 2)}
                </pre>
              )}

              {NON_RESTORABLE.has(String(versionsItem?.resource_type || '').toLowerCase()) ? (
                <p className="versions-footnote">{t('vault.versions.nonRestorableFootnote')}</p>
              ) : null}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setVersionsItem(null)}>
            {t('vault.actions.close')}
          </Button>
          <Button className="btn-vault-primary" disabled={restoreDisabled} onClick={askRestoreItem}>
            {t('vault.actions.restoreToThisVersion')}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={Boolean(confirm)}
        title={confirm?.title}
        body={
          confirm?.kind === 'backup-now' ? (
            <div className="vault-backup-confirm">
              <p className="mb-3 text-body-secondary">{t('vault.confirms.backupNow.body')}</p>
              <div className="vault-backup-options" role="radiogroup" aria-label={t('vault.confirms.backupNow.optionsLabel')}>
                <Form.Check
                  type="radio"
                  id="backup-mode-items"
                  name="backup-mode"
                  className="vault-backup-option"
                  label={
                    <span>
                      <strong>{t('vault.confirms.backupNow.individual')}</strong>
                      <span className="vault-backup-option-hint d-block">
                        {t('vault.confirms.backupNow.individualHint')}
                      </span>
                    </span>
                  }
                  checked={backupMode === 'items'}
                  disabled={backupStarting}
                  onChange={() => setBackupMode('items')}
                />
                <Form.Check
                  type="radio"
                  id="backup-mode-pin"
                  name="backup-mode"
                  className="vault-backup-option"
                  disabled={pinCount >= pinMax || backupStarting}
                  label={
                    <span>
                      <strong>
                        {t('vault.confirms.backupNow.restorePoint', {
                          count: pinCount,
                          max: pinMax,
                        })}
                      </strong>
                      <span className="vault-backup-option-hint d-block">
                        {pinCount >= pinMax
                          ? t('vault.pin.atMaxHint')
                          : t('vault.confirms.backupNow.restorePointHint')}
                      </span>
                    </span>
                  }
                  checked={backupMode === 'pin'}
                  onChange={() => setBackupMode('pin')}
                />
              </div>
              {backupMode === 'pin' && pinCount < pinMax ? (
                <Form.Control
                  className="mt-3"
                  placeholder={t('vault.pin.labelPlaceholder')}
                  value={pinLabel}
                  onChange={(e) => setPinLabel(e.target.value)}
                  maxLength={80}
                  disabled={backupStarting}
                />
              ) : null}
            </div>
          ) : (
            confirm?.body
          )
        }
        confirmLabel={confirm?.confirmLabel}
        variant={confirm?.variant || 'primary'}
        loading={confirm?.kind === 'backup-now' ? backupStarting : restoring}
        onCancel={() => {
          if (restoring || backupStarting) return;
          setConfirm(null);
        }}
        onConfirm={runConfirmed}
      />
    </div>
  );
}
